import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './Login.css';

function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Please fill in all fields.');
      return;
    }

    setIsLoading(true);
    try {
      const user = await login(username, password);

      if (user.role !== 'admin') {
        // Log the practitioner back out — this portal is admin-only
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        throw new Error('Access denied. This portal is for administrators only.');
      }

      navigate('/');
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="login-shell"
      style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}
    >
      <div className="login-bg-decoration" style={{ opacity: 0.06 }}></div>

      <div className="login-container" style={{ maxWidth: '420px' }}>
        {/* Header */}
        <div className="login-header">
          <div
            className="login-logo"
            style={{ background: '#312e81', boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}
          >
            🔐
          </div>
          <h1 className="login-title">Admin Portal</h1>
          <p className="login-subtitle">
            Feelio — System Administration<br />
            <span className="login-subtitle-small">Restricted Access</span>
          </p>
        </div>

        {/* Card */}
        <div className="login-card" style={{ borderTopColor: '#4338ca' }}>
          <h2 className="login-form-title" style={{ color: '#312e81' }}>
            Administrator Login
          </h2>

          {error && (
            <div className="login-error">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            {/* Username */}
            <div className="form-group">
              <label htmlFor="username" className="form-label">
                🪪 Admin Username
              </label>
              <input
                id="username"
                type="text"
                placeholder="Enter admin username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="form-input"
                disabled={isLoading}
                autoFocus
                autoComplete="username"
              />
            </div>

            {/* Password */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                🔒 Password
              </label>
              <div className="password-wrapper">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  style={{ paddingRight: '48px', width: '100%' }}
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="login-btn"
              disabled={isLoading}
              style={{
                background: isLoading
                  ? '#a5b4fc'
                  : 'linear-gradient(135deg, #4338ca 0%, #312e81 100%)',
                boxShadow: '0 6px 20px rgba(67,56,202,0.4)',
              }}
            >
              {isLoading ? '🔄 Verifying...' : '🔐 Sign In as Admin'}
            </button>

            {/* Back to practitioner login */}
            <p style={{ textAlign: 'center', fontSize: '14px', color: '#4a5568', margin: '4px 0 0', fontWeight: '600' }}>
              Not an admin?{' '}
              <Link to="/login" style={{ color: '#4338ca', textDecoration: 'none', fontWeight: '800' }}>
                Practitioner Login
              </Link>
            </p>
          </form>
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p>
            🛡️ <strong>Admin Portal:</strong> Manage hospital locations, user accounts, and system settings.
          </p>
          <p className="login-footer-secondary">
            Unauthorised access is strictly prohibited.
          </p>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;
